import { Component, computed, inject, input } from "@angular/core"
import { Image, ImageView, SectionEnum, Video } from "redlineLibs"
import { TextAreaEditComponent } from "../shared/fields/text-area/text-area-edit.component"
import { EditorContext, EditorService } from "./services/editor.service"
import { ContentType, reviewFeedbackData } from "redlineLibs"
import { EditorFeedbackComponent } from "./fields/editor-feedback.component"
import { ImageViewComponent } from "../shared/fields/image/image-view.component"
import { EditorMapComponent } from "./fields/editor-map.component"
import { FirestoreReadService } from "../content/firestore-read.service"
import { MediaEditorComponent } from "../shared/fields/media-editor.component"
import { VideoViewComponent } from "../shared/fields/video/video-view.component"
import { StorageService } from "../../services/storage.service"
import { UiInheritDirective } from "ui-inherit"

@Component({
  imports: [
    TextAreaEditComponent,
    ImageViewComponent,
    EditorFeedbackComponent,
    EditorMapComponent,
    MediaEditorComponent,
    VideoViewComponent,
    UiInheritDirective,
  ],
  standalone: true,
  template: `
    <div class="column">

      @if (linkTitles().length) {
        <div>
          In response to:
          @for (linkTitle of linkTitles(); track linkTitle; let first = $first) {
            @if (first) {
              {{ linkTitle }}
            }
            @if (!first) {
              <div>
                {{ linkTitle }}
              </div>
            }
          }
        </div>
        <div>
          Authored by: {{ author() }}
        </div>
      }

      @if (adminContext()) {
        <e2e-text-area-edit [rowId]="titleRowId()" context="admin"/>
        <e2e-text-area-edit [rowId]="subTitleRowId()" context="admin"/>
        <e2e-text-area-edit [rowId]="bodyRowId()" context="admin"/>
        <e2e-text-area-edit [rowId]="noteRowId()" context="admin"/>
        <e2e-media-editor [story]="story()" context="admin" inherit/>
        <e2e-editor-map [story]="story()" [zoomLevelInput]="zoomLevel()" inherit/>
      }

      @if (contentContext()) {
        <e2e-text-area-edit [rowId]="titleRowId()"/>
        <e2e-text-area-edit [rowId]="bodyRowId()"/>
        <e2e-media-editor [story]="story()" inherit/>
        <e2e-editor-map [story]="story()" [zoomLevelInput]="zoomLevel()"/>
      }

      @if (contributorContext()) {
        @if (localMap()) {
          <e2e-editor-map [story]="story()" [zoomLevelInput]="zoomLevel()"/>
          <e2e-text-area-edit [rowId]="titleRowId()"/>
          <e2e-text-area-edit [rowId]="bodyRowId()"/>
          <e2e-media-editor [story]="story()" inherit/>
        }

        @if (timeline()) {
          <e2e-text-area-edit [rowId]="titleRowId()"/>
          <e2e-text-area-edit [rowId]="bodyRowId()"/>
          <e2e-media-editor [story]="story()" inherit/>
          <e2e-editor-map [story]="story()" [zoomLevelInput]="zoomLevel()" inherit/>
        }

        @if (needsChanges()) {
          <div>
            Reviewer Feedback:
          </div>
          @for (reviewFeedback of reviewFeedbacks(); track reviewFeedback) {
            <div>
              {{ reviewFeedback?.description }}
            </div>
          }
        }
      }

      @if (moderationContext()) {
        Title:
        <textarea class="textarea textarea-bordered" [rows]="1" readonly [value]="title()"></textarea>
        Body Text:
        <textarea class="textarea textarea-bordered" [rows]="6" readonly [value]="body()"></textarea>

        @let imageViewObject = imageView();
        @if (imageViewObject) {
          <div class="column">
            <div>
              Image:
            </div>
            <div>
              alt-text: 
              {{ imageViewObject.alt }}
            </div>
            <e2e-image-view [imageView]="imageViewObject"/>
          </div>
        }
        @if (videoUrl()) {
          Video:
          <e2e-video-view [videoUrl]="videoUrl()"/>
        }

        Location:
        <e2e-editor-map [story]="story()" [zoomLevelInput]="zoomLevel()"/>

        <div>
          Reviewer Feedback Options:
        </div>

        <e2e-editor-feedback/>

      }

    </div>
  `,
  selector: "e2e-editor-content",
})
export class EditorContentComponent {
  private editorService = inject(EditorService)
  private firestoreReadService = inject(FirestoreReadService)
  private storageService = inject(StorageService)

  context = input.required<EditorContext>()

  // close = output()
  // resubmitForReview = output()
  // approveAndClose = output()

  adminContext = computed(() => this.context() === "admin")
  contentContext = computed(() => this.context() === "content")
  contributorContext = computed(() => this.context() === "contributor")
  moderationContext = computed(() => this.context() === "moderation")

  linkTitles = computed<string[]>(() => {
    const linkIds = this.editorService.editorContent()?.settings.links || []
    return linkIds
      .map(linkId =>
        this.firestoreReadService.stories().get(linkId)?.[SectionEnum.TITLE].value || ""
      )
      .filter(Boolean)
  })

  localMap = computed(() =>
    this.editorService.editorContent()?.status.contentTypes.includes(ContentType.LOCAL_MAP)
  )
  timeline = computed(() =>
    this.editorService.editorContent()?.status.contentTypes.includes(ContentType.TIMELINE)
  )

  story = computed(() => {
    const content = this.editorService.editorContent()
    return content && this.firestoreReadService.storyFromContent(content)
  })

  author = computed(() => this.story()?.created.userName)
  title = computed(() => this.story()?.[SectionEnum.TITLE].value)
  titleRowId = computed(() => this.story()?.[SectionEnum.TITLE].rowId || "")
  subTitle = computed(() => this.story()?.[SectionEnum.SUBTITLE].value)
  subTitleRowId = computed(() => this.story()?.[SectionEnum.SUBTITLE].rowId || "")
  body = computed(() => this.story()?.[SectionEnum.BODY].value)
  bodyRowId = computed(() => this.story()?.[SectionEnum.BODY].rowId || "")
  note = computed(() => this.story()?.[SectionEnum.NOTE].value)
  noteRowId = computed(() => this.story()?.[SectionEnum.NOTE].rowId || "")

  imageView = computed<ImageView | undefined>(() => {
    const image = this.editorService.editorContent()?.rows?.[this.imageRowId()] as Image | undefined
    return image && {
      ...image,
      contentWidth: this.editorService.editorContent()?.settings.width?.px || 0,
    }
  })
  imageRowId = computed(() => this.story()?.[SectionEnum.IMAGE].rowId || "")

  videoRowId = computed(() => this.story()?.[SectionEnum.VIDEO].rowId || "")
  filePath = computed(() =>
    (this.editorService.editorContent()?.rows[this.videoRowId()] as Video)?.filePath)
  videoUrl = computed<string>(() =>
    this.storageService.getFileUrl(this.filePath())
  )

  saving = this.editorService.saving
  preSaving = this.editorService.preSaving
  needsChanges = this.editorService.needsChanges
  reviewFeedbacks = computed(() => {
    const content = this.editorService.editorContent()
    return (
      (content &&
        content.status.reviewFeedbacks.map((reviewFeedback) =>
          reviewFeedbackData.find((data) => data.identifier === reviewFeedback),
        )) ||
      []
    )
  })

  zoomLevel = computed(() => {
    switch (this.context()) {
      case "contributor":
        return 15
      case "admin":
        return 11
      case "content":
        return 11
      case "moderation":
        return 11
      case "myContent":
        return 11
      default:
        return 11
    }
  })

}
